<template>
  <div>
    <!-- TABLE STICKY HEADER -->
    <v-card
      v-if="desktopViewBreakpoint"
      ref="stickyElement"
      v-sticky
      sticky-z-index="5"
      :sticky-offset="`{top: ${offset}}`"
      dark
      color="navy-medium"
      class="text-center pa-2 results-header"
    >
      <v-row
        dense
        class="flex-nowrap"
      >
        <v-col
          v-for="(column, index) of columns"
          :key="`${column.name}-${index}`"
          class="d-flex flex-column justify-center table-right-border table-right-border-dark
          result-columns-align-width is-heading"
        >
          <v-tooltip
            v-if="column.message"
            bottom
            color="#000000"
          >
            <template #activator="{ on, attrs }">
              <h3
                v-bind="attrs"
                v-on="on"
              >
                <span class="body-2 has-pre-line-text">
                  {{ column.name }}
                </span>
              </h3>
            </template>
            <span> {{ column.message }}</span>
          </v-tooltip>
          <button
            v-else
            type="button"
            :style="!column.details && {'cursor': 'default'}"
            :disabled="!column.details"
            @click="$emit('open-drawer-details', ['details-description', column.details])"
          >
            <span
              class="body-2"
              :class="{'has-pre-line-text': hasPreLineText}"
              v-html="tableColumnHeading(column.name)"
            />
          </button>
        </v-col>
      </v-row>
    </v-card>
    <!-- CHOSEN QUOTES -->
    <div
      v-if="isBrokerResultsSectionShowed"
      class="chosen-quotes mt-4"
    >
      <!-- SUBHEADING (FOUND BROKER QUOTES) -->
      <div
        :class="desktopViewBreakpoint && 'mt-4'"
      >
        <results-info
          :quote-ref="quoteRef"
          :quotes-qty="brokerResultsQty"
          :has-quote-summary="hasQuoteSummary"
          :is-no-results="!hasBrokerResults"
          :insured-name="insuredName"
          :product-name="productName"
          :is-details-hidden="hasBrokerResults"
          :is-cta-hidden="!hasBrokerResults && !isNoResults"
          :custom-info-message="customInfoMessage"
          @go-to-form="$emit('go-to-form')"
          @open-drawer-details="$emit('open-drawer-details', $event)"
        >
          <!--   Pass slot to grandchild     -->
          <template
            v-if="hasQuoteSummary"
            slot="successMessage"
          >
            <slot name="brokerResultsMessage" />
          </template>
        </results-info>
      </div>
      <!-- BROKER SOLUTION PARTNER MESSAGE -->
      <template v-if="hasBrokerResults">
        <p v-if="$slots.partnerMessage">
          <slot name="partnerMessage" />
        </p>
        <!-- BROKER RESULTS TABLE-->
        <slot name="brokerResults" />
      </template>
    </div>

    <!-- SUBHEADING (FOUND QUOTES & APPLIED FILTERS) -->
    <div
      v-if="$slots.QZMessage"
      :class="desktopViewBreakpoint && 'mt-4'"
    >
      <results-info
        :quote-ref="quoteRef"
        :insured-name="insuredName"
        :all-results-qty="allResultsQty"
        :is-no-results="isNoResults"
        :product-name="productName"
        :quotes-qty="quotesQty"
        :has-quote-summary="hasQuoteSummary"
        :telematics-quantity="telematicsQuantity"
        :selected-filters="selectedOptionalExtras"
        :is-details-hidden="isBrokerResultsSectionShowed"
        :custom-info-message="customInfoMessage"
        @go-to-form="$emit('go-to-form')"
        @open-drawer-details="$emit('open-drawer-details', $event)"
      >
        <!--   Pass slot to grandchild     -->
        <template
          v-if="hasQuoteSummary"
          slot="successMessage"
        >
          <slot name="QZMessage" />
        </template>
      </results-info>
    </div>

    <!-- FOUND SUCCEEDED QUOTES -->
    <slot name="quotes" />

    <!-- FAILED QUOTES -->
    <template v-if="!hasBrokerResults">
      <div
        v-for="failedQuote of failedQuotes"
        :key="failedQuote.id"
        class="quote-container mb-6"
      >
        <results-failed :failedquote="failedQuote" />
      </div>
    </template>
  </div>
</template>

<script>
import ResultsFailed from '@/components/car/Results/ResultsFailedQuote.vue';
import ResultsInfo from '@/components/car/Results/ResultsInfo';

export default {
  name: 'results-table',
  components: {
    ResultsFailed,
    ResultsInfo,
  },
  inject: [
    'constants',
  ],
  props: {
    quoteRef: {
      type: String,
      default: null,
    },
    columns: {
      type: Array,
      required: true,
    },
    quotesQty: {
      type: Number,
      default: 0,
    },
    failedQuotes: {
      type: Array,
      required: true,
    },
    selectedOptionalExtras: {
      type: Array,
      required: true,
    },
    fixedTopIndent: {
      type: Number,
      required: true,
    },
    insuredName: {
      type: String,
      default: '',
    },
    allResultsQty: {
      type: Number,
      default: 0,
    },
    isNoResults: {
      type: Boolean,
      required: false,
    },
    hasQuoteSummary: {
      type: Boolean,
      required: true,
    },
    telematicsQuantity: {
      type: Number,
      default: 0,
    },
    hasBrokerResults: {
      type: Boolean,
      default: false,
    },
    isBrokerSolutionMessageShown: {
      type: Boolean,
      default: false,
    },
    isBrokerSolutionTableShown: {
      type: Boolean,
      default: false,
    },
    brokerResultsQty: {
      type: Number,
      default: 0,
    },
    mobileResultsBreakpoint: {
      type: Number,
      default: 0,
    },
    productName: {
      type: String,
      default: 'car',
    },
    hasPreLineText: {
      type: Boolean,
      default: true,
    },
    customInfoMessage: {
      type: String,
      default: '',
    },
    coverTypeRelatedLabel: {
      type: String,
      default: '',
    },
  },
  computed: {
    /* since bicycle product had too many columns
     we need to change indent on a different screen width */
    breakpointToSwitchStickyTableIndent () {
      if (this.productName === 'bicycle') {
        return this.$vuetify.breakpoint.width >= this.constants.RESULTS_SIDEBAR_BREAKPOINT;
      }

      return this.$vuetify.breakpoint.lgAndUp;
    },
    tableStickyHeaderTopOffset: {
      get () {
        if (!this.$refs.stickyElement) return null;

        return this.$refs.stickyElement.$el['@@vue-sticky-directive'].options.topOffset;
      },
      set (val) {
        if (this.$refs.stickyElement) {
          this.$refs.stickyElement.$el['@@vue-sticky-directive'].options.topOffset = val;
        }
      },
    },
    offset () {
      return this.breakpointToSwitchStickyTableIndent
        ? this.fixedTopIndent
        : 16;
    },
    isBrokerResultsSectionShowed () {
      return (this.isBrokerSolutionTableShown || this.isBrokerSolutionMessageShown);
    },
    desktopViewBreakpoint () {
      if (this.mobileResultsBreakpoint) {
        return this.$vuetify.breakpoint.width >= this.mobileResultsBreakpoint;
      }
      return this.$vuetify.breakpoint.mdAndUp;
    },
  },
  watch: {
    // fix for non-reactive offset on page resize
    // https://github.com/mehwww/vue-sticky-directive/issues/13#issuecomment-659650921
    breakpointToSwitchStickyTableIndent () {
      this.tableStickyHeaderTopOffset = this.offset;
    },
  },
  methods: {
    tableColumnHeading (string) {
      const splitHeading = string.split(' ');
      return splitHeading.length > 2
        ? string
        : splitHeading.join('\n');
    },
  },
};
</script>

<style scoped>
.has-pre-line-text {
  white-space: pre-line;
}
</style>
